import React from "react";
import { Table, TableBody, TableCell, TableRow, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { formatDelta } from "#utils/checkDetail.utils";
import * as styles from "./styles_vanilla.css";
import type { CheckOutcomeEnum } from "#graphql/supergraphTypes";
import type { FootingComponentValue } from "#viewModelTypes";

export type FootingDetailsTableProps = {
  componentValues: FootingComponentValue[];
  /** DDG `expectedValue` numeric string (calculated total), reformatted to match
   *  the addends; "—" when absent, verbatim when non-numeric. */
  expectedValue: string | null;
  /** DDG `actualValue` numeric string (printed total), reformatted to match the
   *  addends; "—" when absent, verbatim when non-numeric. */
  actualValue: string | null;
  outcome: CheckOutcomeEnum;
  checkName: string;
  rowLabel?: string | null;
  delta?: number | null;
};

const EM_DASH = "—";

const amountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatComponentValue(value: number | null, isPercentage: boolean): string {
  if (value == null) {
    return EM_DASH;
  }
  return isPercentage ? `${(value * 100).toFixed(2)}%` : amountFormatter.format(value);
}

/**
 * Parses a DDG-preformatted numeric string (e.g. `"1,234.00"`, `"(50.00)"`,
 * `"$1,000"`, `"12.34%"`) into a number, mirroring DDG's own parsing so a
 * percent string normalizes back to a fraction. Returns null when the string
 * is not numeric (e.g. `"missing"`), so the caller can fall back to it verbatim.
 */
function parseNumericValue(raw: string): number | null {
  const trimmed = raw.trim();
  const isPercent = trimmed.endsWith("%");
  let body = (isPercent ? trimmed.slice(0, -1) : trimmed).replace(/[$,\s]/g, "");
  let negative = false;
  if (body.startsWith("(") && body.endsWith(")")) {
    negative = true;
    body = body.slice(1, -1);
  }
  // `Number("")` is 0, so a blank or formatting-only string ("$", "()") would
  // otherwise report a missing total as a real zero instead of falling back.
  if (body === "") {
    return null;
  }
  const parsed = Number(body);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const signed = negative ? -parsed : parsed;
  return isPercent ? signed / 100 : signed;
}

/**
 * Renders a calculated/printed total through the same formatting as the addends
 * so the "show your work" table is internally consistent (DDG stringifies totals
 * with fixed 2-decimal money formatting, which otherwise clashes with the
 * addends' variable-precision formatting). Falls back to DDG's raw string when
 * it is not numeric.
 */
function formatTotal(raw: string | null, isPercentage: boolean): string {
  if (raw == null) {
    return EM_DASH;
  }
  const parsed = parseNumericValue(raw);
  return parsed == null ? raw : formatComponentValue(parsed, isPercentage);
}

export type FootingCaptionProps = {
  rowLabel: string | null;
  delta: number | null;
  isPercentage?: boolean;
};

/** Context line above the details: row label and/or signed delta. `isPercentage`
 *  keeps the delta formatted like the addends/totals for percentage footings. */
export const FootingCaption: React.FC<FootingCaptionProps> = ({
  rowLabel,
  delta,
  isPercentage = false,
}) => {
  if (rowLabel == null && delta == null) {
    return null;
  }
  return (
    <span className={styles.footingCaption}>
      {rowLabel != null && (
        <Text variant="secondary-sm" asSpan>
          Row: {rowLabel}
        </Text>
      )}
      {delta != null && (
        <Text variant="secondary-sm" asSpan>
          Difference: {formatDelta(delta, isPercentage)}
        </Text>
      )}
    </span>
  );
};

const MatchResult: React.FC<{ outcome: CheckOutcomeEnum }> = ({ outcome }) => {
  if (outcome === "PASS") {
    return (
      <span className={`${styles.matchResult} ${styles.matchResultPass}`}>
        <Icon type="check" size="small" color={Colors.Success500} />
        Yes
      </span>
    );
  }
  if (outcome === "FAIL") {
    return (
      <span className={`${styles.matchResult} ${styles.matchResultFail}`}>
        <Icon type="times" size="small" color={Colors.Danger500} />
        No
      </span>
    );
  }
  return <span className={`${styles.matchResult} ${styles.matchResultNeutral}`}>N/A</span>;
};

/**
 * Footing "show your work": the contributing addend rows plus the calculated
 * total, printed total, and match verdict, mirroring the predecessor UI. Only the
 * values behind the check are shown — never a full-grid dump — and pass/fail tone
 * is confined to the match-result row so a PASS never renders red.
 */
export const FootingDetailsTable: React.FC<FootingDetailsTableProps> = ({
  componentValues,
  expectedValue,
  actualValue,
  outcome,
  checkName,
  rowLabel = null,
  delta = null,
}) => {
  const isPercentage = checkName.includes("%");
  return (
    <>
      <FootingCaption rowLabel={rowLabel} delta={delta} isPercentage={isPercentage} />
      <Table condensed className={`${styles.detailTable} ${styles.footingTable}`}>
        <TableBody>
          {componentValues.map((cv) => (
            <TableRow key={`${cv.rowIndex}-${cv.colIndex}`}>
              <TableCell>{cv.label}</TableCell>
              <TableCell rightAligned nowrap>
                {formatComponentValue(cv.value, isPercentage)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className={styles.footingSummaryRow}>
            <TableCell>Calculated total</TableCell>
            <TableCell rightAligned nowrap>
              {formatTotal(expectedValue, isPercentage)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Printed total</TableCell>
            <TableCell rightAligned nowrap>
              {formatTotal(actualValue, isPercentage)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Match result</TableCell>
            <TableCell rightAligned nowrap>
              <MatchResult outcome={outcome} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};
